<template>
  <v-card
    rounded="lg"
    class="usage-card d-flex bg-neutral-98 align-center px-4"
  >
    <div class="usage-card__highlight">
      <div v-if="finances.plan === 'paid'">
          <h4>{{ finances.used }}</h4>
          <p>Tokens usados</p>
      </div>
      <div v-else class="d-flex px-4 ga-4 plan-text align-center">
        <div ref="freeChart" style="width: 70px; height: 70px"></div>
        <div class="py-4">
          <h4>{{ finances.used }}%</h4>
          <p>do <span>Plano Gratuito</span> <br> utilizado.</p>
        </div>
      </div>
    </div>
    <v-divider vertical color="##e1e3e2" class="h-100"></v-divider>
    <div class="usage-card__details d-flex flex-column h-100 justify-center ga-2">
        <div class="detail-section">
          <h4>Dia mais usado:</h4>
          <p>
            {{ formatDate(finances.mostUsedDay?.date) }}
            {{ finances.mostUsedDay?.value !== null && finances.mostUsedDay?.value !== undefined
              ? ` (${formatValue(finances.mostUsedDay.value)})`
              : '' }}
          </p>
      </div>
        <div class="detail-section">
          <h4>Média:</h4>
          <p v-if="finances.average">{{ formatValue(finances.average) }}/dia</p>
          <p v-else>Sem informação.</p>
        </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import * as echarts from 'echarts';
import { useFinanceStore } from '@/assistents/stores/finance';

const finances = useFinanceStore();
const freeChart = ref(null);
let chartInstance = null;

const formatDate = (dateString) => {
  if (!dateString || dateString === null) return 'Sem informação.';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return 'Sem informação.';
  if (finances.plan === 'paid') {
    return `${value} tokens`;
  }
  return `${value}%`;
};

const getGradientColors = (value) => {
  switch (true) {
    case value === 100:
      return [
        { offset: 0, color: "#ADFFFE" },
        { offset: 1, color: "#005C5C" },
      ];
    case value > 70:
      return [
        { offset: 0, color: "#DE3730" },
        { offset: 1, color: "#93000A" },
      ];
    case value > 50:
      return [
        { offset: 0, color: "#FF897D" },
        { offset: 1, color: "#DE3730" },
      ];
    default:
      return [
        { offset: 0, color: "#C8FF9E" },
        { offset: 1, color: "#347700" },
      ];
  }
};

const getTextColor = (value) => {
  if (finances.plan === 'paid') {
    return '#007B38'
  }

  switch (true) {
    case value === 100:
      return "#005C5C";
    case value > 70:
      return "#93000A";
    case value > 50:
      return "#DE3730";
    default:
      return "#347700";
  }
};

const currentTextColor = computed(() => getTextColor(finances.used));

const renderChart = () => {
  if (!freeChart.value || finances.plan === 'paid') return;

  if (!chartInstance) {
    chartInstance = echarts.init(freeChart.value);
  }

  chartInstance.setOption({
    series: [
      {
        type: "gauge",
        startAngle: 0,
        endAngle: -360,
        max: 100,
        radius: "100%",
        progress: {
          show: true,
          roundCap: true,
          width: 10,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 1,
              y2: 1,
              colorStops: getGradientColors(finances.used),
            },
          },
        },
        axisLine: { show: false },
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: { show: false },
        data: [{ value: finances.used }],
      },
    ],
  });
};

onMounted(() => {
  renderChart();
});

watch(
  () => [finances.used, finances.plan],
  () => {
    renderChart();
  }
);
</script>

<style scoped lang="scss">
.usage-card {
  box-shadow:
    0px 1px 3px 1px #00141426,
    0px 1px 2px 0px #006a6a4d;
  height: 120px;

}

.usage-card__highlight {
  min-width: 152px;
  text-align: start;
  color: #5c5f5f;
  font-family: Roboto;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0%;

  h4 {
    font-family: Poppins;
    font-weight: 600;
    font-size: 31px;
    line-height: 56.4px;
    color: v-bind("currentTextColor");
  }

  p {
    span {
      color: v-bind("currentTextColor");
      font-weight: 700;
    }
  }
}

.usage-card__details {
  min-width: 180px;
  padding-left: 16px;
  display: flex;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0%;
  text-align: start;
  color: #232626;

  h4 {
    font-weight: 700;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    font-size: 12px;
    font-weight: 400;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }
}

.plan-text {
  white-space: nowrap;
}

</style>